/**
 * Checkout Step Component
 * Displays provider checkout iframe or redirect button.
 * Iframe/widget checkouts render in a full-screen portal overlay.
 *
 * Provider-specific widget behaviour (URL format, post-load handshakes,
 * iframe attributes) is resolved dynamically via widget-adapters — this
 * component stays fully gateway-agnostic.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useRamp } from '../context/RampContext';
import { RedirectScreen } from './form/RedirectScreen';
import { Spinner } from './ui/Spinner';
import { useT } from '../i18n';
import { getWidgetAdapter } from '../providers/widget-adapters';

export interface CheckoutStepProps {
	className?: string;
	autoRedirect?: boolean;
	redirectDelay?: number;
}

export function CheckoutStep({
	className = '',
	autoRedirect = false,
	redirectDelay = 3000,
}: CheckoutStepProps) {
	const { order, orderStatus, goToStep, goBack } = useRamp();
	const t = useT();
	const [iframeLoaded, setIframeLoaded] = useState(false);
	const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

	const isExpired = order?.checkout.expiresAt ? Date.now() > order.checkout.expiresAt : false;

	// Handle auto-redirect
	useEffect(() => {
		if (order?.checkout.method === 'redirect' && autoRedirect && !isExpired) {
			setRedirectCountdown(Math.ceil(redirectDelay / 1000));
			const timer = setTimeout(() => {
				window.location.href = order.checkout.url;
			}, redirectDelay);

			const countdownInterval = setInterval(() => {
				setRedirectCountdown((prev) => (prev && prev > 1 ? prev - 1 : null));
			}, 1000);

			return () => {
				clearTimeout(timer);
				clearInterval(countdownInterval);
			};
		}
	}, [order, autoRedirect, redirectDelay, isExpired]);

	// Navigate on terminal statuses
	useEffect(() => {
		if (!orderStatus) return;
		if (orderStatus.status === 'completed') {
			goToStep('complete');
		} else if (['failed', 'cancelled', 'expired'].includes(orderStatus.status)) {
			goToStep('error');
		}
	}, [orderStatus?.status, goToStep]);

	if (!order) {
		return (
			<div
				className={`flex flex-col items-center justify-center p-8 ${className}`}
				style={{ color: 'var(--t-text)' }}
			>
				<p className="text-[15px] mb-4">{t('checkout.noOrder')}</p>
				<button
					className="font-semibold text-[14px] transition-all"
					style={{
						height: 48,
						borderRadius: 'var(--t-cta-radius, 100px)',
						background: 'var(--t-surface)',
						color: 'var(--t-text)',
						paddingLeft: 24,
						paddingRight: 24,
					}}
					onClick={goBack}
				>
					{t('checkout.goBack')}
				</button>
			</div>
		);
	}

	if (isExpired) {
		return (
			<div
				className={`flex flex-col items-center justify-center p-8 text-center ${className}`}
				style={{ color: 'var(--t-text)' }}
			>
				{/* <div className="text-[48px] mb-4">&#9200;</div> */}
				<h3 className="text-[18px] font-semibold mb-2">{t('checkout.expired')}</h3>
				<p className="text-[14px] mb-6" style={{ color: 'var(--t-text-secondary)' }}>
					{t('checkout.expiredDesc')}
				</p>
				<button
					className="w-full font-semibold text-[16px] transition-all"
					style={{
						height: 60,
						borderRadius: 'var(--t-cta-radius, 100px)',
						background: 'var(--t-cta-bg)',
						color: 'var(--t-cta-text)',
					}}
					onClick={goBack}
				>
					{t('checkout.startOver')}
				</button>
			</div>
		);
	}

	// Iframe checkout — full-screen portal overlay
	if (order.checkout.method === 'iframe') {
		const iframeSrc = order.checkout.url;
		const allowAttr =
			order.checkout.allowedFeatures?.join('; ') ||
			'payment; camera; microphone; geolocation; clipboard-read; clipboard-write; accelerometer; gyroscope; magnetometer; fullscreen; autoplay; encrypted-media; web-share';

		return createPortal(
			<div
				style={{
					position: 'fixed',
					inset: 0,
					zIndex: 9999,
					display: 'flex',
					flexDirection: 'column',
					background: '#000',
				}}
			>
				{/* Top bar with back button */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						height: 48,
						paddingLeft: 8,
						paddingRight: 16,
						background: '#111',
						flexShrink: 0,
					}}
				>
					<button
						onClick={goBack}
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 6,
							background: 'none',
							border: 'none',
							color: '#fff',
							fontSize: 14,
							fontWeight: 500,
							cursor: 'pointer',
							padding: '8px 12px',
							borderRadius: 6,
						}}
					>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
							<path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
						{t('checkout.cancel')}
					</button>
				</div>

				{/* Iframe fills remaining space */}
				<div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
					{!iframeLoaded && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Spinner size={32} />
							<p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>
								{t('checkout.loading')}
							</p>
						</div>
					)}
					<iframe
						src={iframeSrc}
						style={{
							display: iframeLoaded ? 'block' : 'none',
							width: '100%',
							height: '100%',
							border: 'none',
						}}
						allow={allowAttr}
						onLoad={() => setIframeLoaded(true)}
						title={t('checkout.iframeTitle')}
					/>
				</div>
			</div>,
			document.body
		);
	}

	// Redirect checkout
	if (order.checkout.method === 'redirect') {
		return (
			<RedirectScreen
				gatewayName={order.gateway}
				checkoutUrl={order.checkout.url}
				onCancel={goBack}
				redirectCountdown={redirectCountdown}
			/>
		);
	}

	// Widget checkout — full-screen portal overlay
	if (order.checkout.method === 'widget' && order.widgetConfig) {
		return createPortal(
			<WidgetIframeOverlay
				order={order}
				iframeLoaded={iframeLoaded}
				onIframeLoad={() => setIframeLoaded(true)}
				onBack={goBack}
				t={t}
			/>,
			document.body
		);
	}

	return (
		<div
			className={`flex flex-col items-center justify-center p-8 ${className}`}
			style={{ color: 'var(--t-text)' }}
		>
			<p className="text-[15px] mb-4">{t('checkout.unknownMethod')}</p>
			<button
				className="font-semibold text-[14px] transition-all"
				style={{
					height: 48,
					borderRadius: 'var(--t-cta-radius, 100px)',
					background: 'var(--t-surface)',
					color: 'var(--t-text)',
					paddingLeft: 24,
					paddingRight: 24,
				}}
				onClick={goBack}
			>
				{t('checkout.goBack')}
			</button>
		</div>
	);
}

/**
 * Gateway-agnostic widget iframe overlay.
 * Resolves the correct WidgetAdapter for the gateway and delegates URL
 * construction, iframe attributes, and post-load handshakes to it.
 */
function WidgetIframeOverlay({
	order,
	iframeLoaded,
	onIframeLoad,
	onBack,
	t,
}: {
	order: { checkout: { url: string; allowedFeatures?: string[] }; widgetConfig?: Record<string, unknown>; gateway: string };
	iframeLoaded: boolean;
	onIframeLoad: () => void;
	onBack: () => void;
	t: ReturnType<typeof useT>;
}) {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const adapter = useMemo(() => getWidgetAdapter(order.gateway), [order.gateway]);

	const widgetUrl = adapter.buildUrl(order.checkout.url, order.widgetConfig ?? {});
	const widgetOrigin = order.checkout.url;
	const allowAttr = order.checkout.allowedFeatures?.join('; ')
		|| adapter.iframeAllow
		|| 'payment; camera; microphone';

	const handleLoad = useCallback(() => {
		onIframeLoad();
		if (iframeRef.current && adapter.onLoad) {
			adapter.onLoad(iframeRef.current, widgetOrigin);
		}
	}, [onIframeLoad, adapter, widgetOrigin]);

	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				zIndex: 9999,
				display: 'flex',
				flexDirection: 'column',
				background: '#000',
			}}
		>
			{/* Top bar with back button */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					height: 48,
					paddingLeft: 8,
					paddingRight: 16,
					background: '#111',
					flexShrink: 0,
				}}
			>
				<button
					onClick={onBack}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						background: 'none',
						border: 'none',
						color: '#fff',
						fontSize: 14,
						fontWeight: 500,
						cursor: 'pointer',
						padding: '8px 12px',
						borderRadius: 6,
					}}
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
					{t('checkout.cancel')}
				</button>
			</div>

			{/* Iframe fills remaining space */}
			<div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
				{!iframeLoaded && (
					<div
						style={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Spinner size={32} />
						<p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>
							{t('checkout.loading')}
						</p>
					</div>
				)}
				<iframe
					ref={iframeRef}
					src={widgetUrl}
					style={{
						display: iframeLoaded ? 'block' : 'none',
						width: '100%',
						height: '100%',
						border: 'none',
					}}
					allow={allowAttr}
					sandbox={adapter.iframeSandbox}
					onLoad={handleLoad}
					title={t('checkout.widgetTitle')}
				/>
			</div>
		</div>
	);
}
